<template>
  <div class="embedded-signup">
    <page-header
      :header-title="$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.DESC')"
    />

    <div class="medium-8 columns">
      <warning-callout
        :title="$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.NOTICE.TITLE')"
      >
        <p
          v-dompurify-html="
            $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.NOTICE.TEXT')
          "
        />
      </warning-callout>

      <ol class="embedded-signup__steps">
        <li v-for="step in steps" :key="step.number">
          <span class="embedded-signup__step-number">{{ step.number }}</span>
          <div>
            <p class="embedded-signup__step-title">{{ step.title }}</p>
            <p class="embedded-signup__step-desc">{{ step.desc }}</p>
          </div>
        </li>
      </ol>

      <label :class="{ error: $v.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INBOX_NAME.LABEL') }}
        <input
          v-model.trim="inboxName"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INBOX_NAME.PLACEHOLDER')
          "
          @blur="$v.inboxName.$touch"
        />
        <p v-if="!$v.inboxName.$error" class="help-text">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INBOX_NAME.HELP') }}
        </p>
        <span v-if="$v.inboxName.$error" class="message">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INBOX_NAME.ERROR') }}
        </span>
      </label>

      <label>
        {{
          $t(
            'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.LABEL'
          )
        }}
        <select v-model="enableDataExtraction">
          <option :value="true">
            {{
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.ENABLED'
              )
            }}
          </option>
          <option :value="false">
            {{
              $t(
                'INBOX_MGMT.ADD.WEBSITE_CHANNEL.CHANNEL_ENABLE_DATA_EXTRACTION.DISABLED'
              )
            }}
          </option>
        </select>
        <p class="help-text">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.MANUAL_SETUP.INSIGHTS_HELP') }}
        </p>
      </label>

      <woot-submit-button
        :loading="isProcessing"
        :disabled="$v.$invalid"
        icon-class="brand-facebook"
        :button-text="
          isProcessing
            ? $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.IN_PROGRESS')
            : $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUBMIT_BUTTON')
        "
        type="button"
        @click="startSignup"
      />
    </div>
  </div>
</template>

<script>
import { maxLength } from 'vuelidate/lib/validators';
import alertMixin from 'shared/mixins/alertMixin';
import PageHeader from '../../SettingsSubPageHeader';
import WarningCallout from 'dashboard/components/ui/WarningCallout';
import router from '../../../../index';
import embeddedSignupMixin from './whatsapp/embeddedSignupMixin';
import { UNSUPPORTED_FLOW } from './whatsapp/embeddedSignupUtils';

// Duración del aviso de "bandeja creada" cuando el usuario ya salió del alta.
const BACKGROUND_TOAST_MS = 10000;

export default {
  components: {
    PageHeader,
    WarningCallout,
  },
  mixins: [alertMixin, embeddedSignupMixin],
  // Si el usuario sale de la pantalla con el popup abierto, Meta termina igual su parte: el alta
  // se completa y se avisa con un toast en lugar de redirigir.
  keepSignupOnDestroy: true,
  data() {
    return {
      isProcessing: false,
      inboxName: '',
      enableDataExtraction: false,
    };
  },
  validations: {
    // Opcional: vacío toma el nombre del negocio en Meta. El tope es el mismo del alta manual.
    inboxName: { maxLength: maxLength(30) },
  },
  computed: {
    // Los tres pasos que verá el usuario dentro del popup de Meta.
    steps() {
      return [1, 2, 3].map(number => ({
        number,
        title: this.$t(
          `INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.STEPS.${number}.TITLE`
        ),
        desc: this.$t(
          `INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.STEPS.${number}.DESC`
        ),
      }));
    },
  },
  created() {
    // Fuera de data(): solo decide si se redirige al terminar, no se pinta.
    this.hasLeftScreen = false;
    // El usuario ya eligió el camino asistido: el SDK se descarga ahora para que el clic abra el popup.
    this.preloadSignupSdk();
  },
  // Marca la salida para que un alta que termina después no saque al usuario de donde navegó.
  beforeDestroy() {
    this.hasLeftScreen = true;
  },
  methods: {
    async startSignup() {
      if (this.isProcessing) return;
      this.isProcessing = true;

      try {
        // Abre el popup de Meta: login + selección de WABA; si el número ya vive en la
        // app de WhatsApp Business, Meta muestra ahí mismo el QR de coexistencia.
        const signupData = await this.runSignup();
        // null = el usuario cerró o canceló el popup en cualquier punto.
        if (!signupData) {
          this.showAlert(
            this.$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED')
          );
          return;
        }

        const payload = {
          ...signupData,
          enable_data_extraction: this.enableDataExtraction,
        };
        if (this.inboxName) payload.inbox_name = this.inboxName;

        const inbox = await this.$store.dispatch(
          'inboxes/createWhatsAppEmbeddedSignup',
          payload
        );
        // Meta terminó pero no aceptó los webhooks: la bandeja existe y se repara con Reconectar.
        if (inbox.reauthorization_required) {
          this.showAlert(
            this.$t(
              'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CREATED_DISCONNECTED'
            )
          );
        }

        this.finishSignup(inbox);
      } catch (error) {
        // Corrida cancelada a propósito: no es un fallo que avisar.
        if (this.isSignupAborted(error)) return;
        this.showAlertError(this.errorMessage(error));
      } finally {
        this.isProcessing = false;
      }
    },
    // Lleva al paso de agentes si el usuario sigue en el alta; si ya navegó a otra pantalla, solo
    // le avisa que la bandeja quedó creada.
    finishSignup(inbox) {
      if (this.hasLeftScreen) {
        // Llega segundos después de cerrar el popup y el usuario ya está en otra cosa: dura más
        // que un toast normal y lleva a la bandeja. Se llama a propósito sobre la instancia ya
        // destruida ($t sigue resolviendo y el toast va por el bus global): no cortar aquí por
        // _isDestroyed o el aviso se pierde sin ruido.
        this.showAlertSuccess(
          this.$t(
            'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CREATED_IN_BACKGROUND',
            { inboxName: inbox.name }
          ),
          {
            type: 'link',
            to: { name: 'settings_inbox_show', params: { inboxId: inbox.id } },
            message: this.$t(
              'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.GO_TO_INBOX'
            ),
          },
          BACKGROUND_TOAST_MS
        );
        return;
      }

      router.replace({
        name: 'settings_inboxes_add_agents',
        params: {
          page: 'new',
          inbox_id: inbox.id,
        },
      });
    },
    // Mensaje según el origen del fallo: flujo sin número, error del backend, error que Meta
    // mostró en el popup, o genérico.
    errorMessage(error) {
      if (error && error.message === UNSUPPORTED_FLOW) {
        return this.$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.UNSUPPORTED');
      }
      return (
        this.signupApiError(error) ||
        this.signupPopupError(error) ||
        this.$t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.ERROR_MESSAGE')
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/variables';

.embedded-signup {
  // Mismos colores del círculo de paso del wizard lateral, en una lista propia.
  &__steps {
    list-style: none;
    margin: 0 0 var(--space-normal);

    li {
      display: flex;
      gap: var(--space-small);
      margin-bottom: var(--space-small);
    }
  }

  &__step-number {
    align-items: center;
    background: $conversa2-blue-600-color;
    border-radius: 20px;
    color: var(--white);
    display: inline-flex;
    flex-shrink: 0;
    font-size: var(--font-size-mini);
    height: var(--space-medium);
    justify-content: center;
    width: var(--space-medium);
  }

  &__step-title {
    color: var(--conversa2-neutral-800-color);
    font-weight: var(--font-weight-medium);
    margin-bottom: 0;
  }

  &__step-desc {
    color: var(--b-600);
    font-size: var(--font-size-small);
    margin-bottom: 0;
  }
}
</style>
